import {Component, inject, OnInit} from '@angular/core';
import {DialogService, DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {FormBuilder, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {RestErrorHelper} from '../../../../helper/rest-error-helper';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ErrorType} from '../../../../model/error-type';
import {DialogHelper} from '../../../shared/helper/dialog-helper';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {LoadingComponent} from '../../../../components/generic/loading/loading.component';
import {MessageComponent} from '../../../../components/generic/message/message.component';
import {DialogButtonComponent} from '../../../shared/common/dialog-button/dialog-button.component';
import {PlayerProfileService} from '../../service/player-profile.service';
import {MandantSettingsService} from '../../../../services/mandant-settings.service';
import {
  PLAYER_PROFILE_SETTING_NOTIFICATION_MATCH_PUSH,
  PLAYER_PROFILE_SETTING_NOTIFICATION_MESSAGE,
  PLAYER_PROFILE_SETTING_NOTIFICATION_NEARBY_MAIL,
  PLAYER_PROFILE_SETTING_NOTIFICATION_NEARBY_PUSH,
  PLAYER_PROFILE_SETTING_NOTIFICATION_PARTICIPATION_MAIL,
  PLAYER_PROFILE_SETTING_NOTIFICATION_PARTICIPATION_PUSH,
  PLAYER_PROFILE_SETTING_NOTIFICATION_STATISTIC
} from '../../model/player-profile-setting';
import {
  PlayerProfileSettingsPushNotificationComponent
} from './player-profile-settings-push-notification/player-profile-settings-push-notification.component';
import {PlayerProfile} from '../../model/player-profile';
import {PlayerSessionService} from '../../service/player-session.service';
import {
  PlayerProfileSettingsItemComponent
} from './player-profile-settings-item/player-profile-settings-item.component';

@Component({
  selector: 'app-player-profile-settings-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    Tabs,
    Tab,
    TabList,
    TabPanels,
    TranslatePipe,
    LoadingComponent,
    MessageComponent,
    DialogButtonComponent,
    TabPanel,
    PlayerProfileSettingsPushNotificationComponent,
    PlayerProfileSettingsItemComponent
  ],
  templateUrl: './player-profile-settings-dialog.component.html',
  styleUrl: './player-profile-settings-dialog.component.scss'
})
export class PlayerProfileSettingsDialogComponent  implements OnInit
{
  public ref = inject(DynamicDialogRef);
  public config = inject(DynamicDialogConfig);
  private formBuilder = inject(FormBuilder);
  private restErrorHelper = inject(RestErrorHelper);
  private playerProfileService = inject(PlayerProfileService);
  private translate = inject(TranslateService);
  private playerSessionService = inject(PlayerSessionService);

  selectedTabValue = 0;
  form: UntypedFormGroup;
  errorMsg: ErrorType;
  isLoading: boolean;
  isSaving: boolean;
  userSettings: any[];
  notificationSettings: any[];
  playerProfile: PlayerProfile;

  isUploading: boolean;
  isAdmin: boolean;

  public static openDialog(dialogService: DialogService, translate: TranslateService): DynamicDialogRef
  {
    return dialogService.open(
      PlayerProfileSettingsDialogComponent,
      DialogHelper.createBasicConfig({
        header: translate.instant('USER_SETTINGS'),
        width: DialogHelper.getDialogWidth(700),
        height: '80%',
        styleClass: 'dynamicDialog'
      }));
  }

  ngOnInit(): void
  {
    this.playerProfile = this.playerSessionService.playerProfileObject;
    this.form = this.formBuilder.group([]);
    this.loadData();
  }

  private loadData(): void
  {
    this.isLoading = true;

    this.playerProfileService.getUserSettings().subscribe({
      next: data =>
      {
        console.log('Benutzer-Einstellungen erhalten', data);
        this.userSettings = data;
        this.isLoading = false;
        this.initForm();
      },
      error: error =>
      {
        console.log('error', error);
        this.isLoading = false;
        this.errorMsg = this.restErrorHelper.getErrorType(error);
      }
    });
  }

  private initForm(): void
  {
    this.notificationSettings = [];
    this.addNotificationSetting(PLAYER_PROFILE_SETTING_NOTIFICATION_STATISTIC, undefined,
      this.translate.instant('STATISTICS'), 'statistic', undefined);

    this.addNotificationSetting(PLAYER_PROFILE_SETTING_NOTIFICATION_PARTICIPATION_MAIL, PLAYER_PROFILE_SETTING_NOTIFICATION_PARTICIPATION_PUSH,
      this.translate.instant('PARTICIPATION'), 'participationMail', 'participationPush');

    this.addNotificationSetting(undefined, PLAYER_PROFILE_SETTING_NOTIFICATION_MATCH_PUSH,
      this.translate.instant('MATCHES'), undefined, 'matchPush');

    this.addNotificationSetting(PLAYER_PROFILE_SETTING_NOTIFICATION_NEARBY_MAIL, PLAYER_PROFILE_SETTING_NOTIFICATION_NEARBY_PUSH,
      this.translate.instant('NEARBY_NOTIFICATION'), 'nearbyMail', 'nearbyPush');

    // this.addNotificationSetting(PLAYER_PROFILE_SETTING_NOTIFICATION_ACHIEVEMENT, this.translate.instant('ACHIEVEMENTS'), 'achievement');
    this.addNotificationSetting(PLAYER_PROFILE_SETTING_NOTIFICATION_MESSAGE, undefined,
      this.translate.instant('MESSAGES'), 'message', undefined);
  }

  private addNotificationSetting(key: string, keyPush: string, label: string, fieldName: string, fieldNamePush: string): void
  {
    this.notificationSettings.push({key, keyPush, label, fieldName, fieldNamePush});
    if (fieldName)
    {
      const value = +MandantSettingsService.getValue(key, this.userSettings, 0);
      this.form.addControl(fieldName, new UntypedFormControl(value));
    }
    if (fieldNamePush)
    {
      const value = +MandantSettingsService.getValue(keyPush, this.userSettings, 0);
      this.form.addControl(fieldNamePush, new UntypedFormControl(value));
    }
  }

  onOkClicked(): void
  {
    this.isSaving = true;
    const body = [];
    for (const notification of this.notificationSettings)
    {
      if (notification.key)
        MandantSettingsService.applySettingValue(body, notification.key, this.form.controls[notification.fieldName].value, this.userSettings, 1);
      if (notification.keyPush)
        MandantSettingsService.applySettingValue(body, notification.keyPush, this.form.controls[notification.fieldNamePush].value, this.userSettings, 1);
    }

    if (body.length === 0)
    {
      console.log('Keine Einstellungen geändert');
      this.isSaving = false;
      this.ref.close(false);
      return;
    }

    this.playerProfileService.saveUserSettings(body).subscribe({
      next: data =>
      {
        console.log('Einstellungen gespeichert', data);
        this.isSaving = false;
        this.ref.close(true);
      },
      error: error =>
      {
        this.isSaving = false;
        this.restErrorHelper.handleErrorService(error);
      }
    });
  }

  onCancelClicked(): void
  {
    if (!this.isSaving)
      this.ref.close(false);
  }
}
